import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/AccountPage.css";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import { normalizeRoleName } from "../utils/roles";
import { useAuth } from "../contexts/AuthContext";
import MyTeamPanel from "../components/MyTeamPanel";
import TeamLeavesPanel from "../components/TeamLeavesPanel";
import SATDashboardWidget from "../components/satRequest/SATDashboardWidget";
import NoirAlert from "../components/NoirAlert";

const toDateOnly = (value) => (value ? String(value).slice(0, 10) : null);

const toLocalDateKey = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const normalizeDateKey = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = new Date(value);
  return toLocalDateKey(parsed);
};

const buildLeaveDayMap = (leaves) => {
  const map = new Map(); // yyyy-mm-dd -> { approved, pending, rejected }

  const mark = (dateKey, status) => {
    if (!dateKey) return;
    const prev = map.get(dateKey) || { approved: 0, pending: 0, rejected: 0 };
    const lower = String(status || "").toLocaleLowerCase("tr-TR");
    if (lower === "onaylandı") prev.approved += 1;
    else if (lower === "beklemede") prev.pending += 1;
    else if (lower === "reddedildi") prev.rejected += 1;
    else prev.pending += 1;
    map.set(dateKey, prev);
  };

  const addRange = (startKey, endKey, status) => {
    if (!startKey) return;
    const start = new Date(`${startKey}T00:00:00`);
    const end = new Date(`${(endKey || startKey)}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    const cur = new Date(start.getTime());
    while (cur <= end) {
      const key = toLocalDateKey(cur);
      mark(key, status);
      cur.setDate(cur.getDate() + 1);
    }
  };

  for (const l of leaves || []) {
    const startKey = toDateOnly(l.start_date);
    const endKey = toDateOnly(l.end_date);
    if (l.total_duration_hours != null) {
      addRange(startKey, startKey, l.status);
    } else {
      addRange(startKey, endKey, l.status);
    }
  }

  return map;
};

const monthMatrix = (year, monthIndex0) => {
  const first = new Date(year, monthIndex0, 1);
  const last = new Date(year, monthIndex0 + 1, 0);
  const daysInMonth = last.getDate();
  const startDow = (first.getDay() + 6) % 7; // Monday=0

  const cells = [];
  for (let i = 0; i < startDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, monthIndex0, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const isWeekend = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return false;
  const day = dateObj.getDay(); // Sun=0..Sat=6
  return day === 0 || day === 6;
};

const holidayShortLabel = (name) => {
  if (!name) return "";
  const trimmed = String(name).trim();
  if (!trimmed) return "";
  const firstWord = trimmed.split(" ")[0];
  return firstWord.length > 3 ? `${firstWord.slice(0, 3)}…` : firstWord;
};

const AccountPage = () => {
  const { t, lang } = useI18n();
  const safeT = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const { user: me, toolPermissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });

  const [leaves, setLeaves] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingApprovalsError, setPendingApprovalsError] = useState("");

  const [calendarStart, setCalendarStart] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const leaveDayMap = useMemo(() => buildLeaveDayMap(leaves), [leaves]);
  const holidayMap = useMemo(() => {
    const map = new Map();
    (publicHolidays || []).forEach((h) => {
      const key = normalizeDateKey(h?.date);
      if (key) map.set(key, h.name);
    });
    return map;
  }, [publicHolidays]);

  const todayKey = useMemo(() => toLocalDateKey(new Date()), []);

  const monthRangeLabel = useMemo(() => {
    const start = new Date(calendarStart.getFullYear(), calendarStart.getMonth(), 1);
    const end = new Date(calendarStart.getFullYear(), calendarStart.getMonth() + 4, 1);
    const locale = lang === "tr" ? "tr-TR" : "en-US";
    const startLabel = start.toLocaleDateString(locale, { month: "long", year: "numeric" });
    const endLabel = end.toLocaleDateString(locale, { month: "long", year: "numeric" });
    return `${startLabel} - ${endLabel}`;
  }, [calendarStart, lang]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const leavesRes = await apiClient.get("/leaves/my");
      setLeaves(leavesRes.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const role = normalizeRoleName(me?.role_name);
    const canSeeApprovals = ["admin", "company_manager", "unit_supervisor", "hr"].includes(role);
    if (!canSeeApprovals) {
      setPendingApprovals([]);
      setPendingApprovalsError("");
      return;
    }

    setPendingApprovalsError("");
    apiClient
      .get("/admin/leaves/pending", { params: { limit: 5 } })
      .then((res) => {
        setPendingApprovals(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        setPendingApprovals([]);
        setPendingApprovalsError(err.response?.data?.msg || err.message || t("common.genericError"));
      });
  }, [me?.role_name, t]);

  useEffect(() => {
    let cancelled = false; // ay değişirken geç gelen eski yanıt ezmesin
    const fetchHolidays = async () => {
      const start = new Date(calendarStart.getFullYear(), calendarStart.getMonth(), 1);
      const end = new Date(calendarStart.getFullYear(), calendarStart.getMonth() + 5, 0);
      const startKey = toLocalDateKey(start);
      const endKey = toLocalDateKey(end);
      try {
        const res = await apiClient.get("/public-holidays", {
          params: { start: startKey, end: endKey },
        });
        if (!cancelled) setPublicHolidays(res.data || []);
      } catch (err) {
        if (!cancelled) setPublicHolidays([]);
      }
    };

    fetchHolidays();
    return () => {
      cancelled = true;
    };
  }, [calendarStart]);

  if (loading) return <div className="account-page">{t("common.loading")}</div>;
  if (error)
    return (
      <div className="account-page error">
        {t("common.error")}: {error}
        <div>
          <button className="ghost" type="button" onClick={load}>
            {t("adminActivities.retry")}
          </button>
        </div>
      </div>
    );

  const locale = lang === "tr" ? "tr-TR" : "en-US";
  const weekdays = lang === "tr"
    ? ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const months = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(calendarStart.getFullYear(), calendarStart.getMonth() + i, 1);
    return {
      key: `${d.getFullYear()}-${d.getMonth()}`,
      date: d,
      label: d.toLocaleDateString(locale, { month: "long", year: "numeric" }),
    };
  });
  const leaveBalances = me?.leave_balances || [];
  const annualDaysBalance = Number(me?.total_leave_quota_days || 0);
  const totalHoursBalance = leaveBalances
    .filter((b) => b.balance_unit === "saat")
    .reduce((sum, b) => sum + Number(b.balance || 0), 0);

  const canLeaveReport = (toolPermissions || []).some(
    (p) => p.tool_key === "leave_report" && p.granted
  );
  const downloadWorkloadReport = async () => {
    setReportBusy(true);
    try {
      const res = await apiClient.get(`/leaves/report/workload.xlsx?t=${Date.now()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `izin-is-gucu-dokumu-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setNotification({
        message: e.response?.data?.msg || e.message || "Rapor indirilemedi.",
        type: "error",
      });
    } finally {
      setReportBusy(false);
    }
  };

  return (
    <div className="account-page">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="account-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2>{t("account.title")}</h2>
          <div className="account-subline">
            <span className="pill">{me?.name} {me?.surname}</span>
            <span className="pill mono">@{me?.username}</span>
            {me?.email && <span className="pill mono">{me.email}</span>}
          </div>
        </div>
        {canLeaveReport && (
          <button
            type="button"
            onClick={downloadWorkloadReport}
            disabled={reportBusy}
            title="Ekip bazlı aylık/haftalık iş gücü ve doluluk raporunu Excel indirir"
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: "1px solid #2c6af0",
              background: reportBusy ? "#9db8f5" : "#2c6af0",
              color: "#fff",
              cursor: reportBusy ? "default" : "pointer",
              fontSize: "0.9rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {reportBusy ? "Hazırlanıyor…" : "⬇ Hızlı İzin Dökümü (Excel)"}
          </button>
        )}
      </div>

      {pendingApprovalsError ? (
        <div className="card">
          <div className="card-head">
            <h3>{t("nav.approvals")}</h3>
          </div>
          <div className="muted">{pendingApprovalsError}</div>
        </div>
      ) : pendingApprovals.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h3>{t("nav.approvals")}</h3>
            <Link className="ghost" to="/admin/leaves">
              {safeT("admin.leaves.title", "Bekleyen izinler")}
            </Link>
          </div>
          <div className="table-wrap">
            <table className="admin-leaves-table">
              <thead>
                <tr>
                  <th>{t("admin.leaves.columns.requester")}</th>
                  <th>{t("admin.leaves.columns.type")}</th>
                  <th>{t("admin.leaves.start")}</th>
                  <th>{t("admin.leaves.end")}</th>
                </tr>
              </thead>
              <tbody>
                {pendingApprovals.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.name} {l.surname}
                    </td>
                    <td>
                      {l.leave_sub_type ? `${l.leave_type} (${l.leave_sub_type})` : l.leave_type}
                    </td>
                    <td className="mono">{String(l.start_date || "").slice(0, 10)}</td>
                    <td className="mono">{String(l.end_date || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="account-summary-row">
        <div className="card summary-card">
          <div className="summary-k">{t("account.hireDate")}</div>
          <div className="summary-v mono">
            {me?.leave_base_date ? String(me.leave_base_date).slice(0, 10) : "-"}
          </div>
        </div>

        <div className="card summary-card">
          <div className="summary-k">{t("account.nextAccrualTitle")}</div>
          <div className="summary-v mono">
            {me?.leave_accrual_start_date ? String(me.leave_accrual_start_date).slice(0, 10) : "-"}
          </div>
          <div className="summary-sub muted">
            {t("account.nextAccrualDaysLabel")}: {me?.next_accrual_entitlement_days ?? "-"}
          </div>
        </div>

        <div className="card summary-card">
          <div className="summary-k">{t("account.totalLeave")}</div>
          <div className="summary-v">
            {annualDaysBalance} <span className="muted">{t("leave.summary.days")}</span>
          </div>
        </div>

        <div className="card summary-card">
          <div className="summary-k">{t("account.totalLeaveHours")}</div>
          <div className="summary-v">
            {totalHoursBalance} <span className="muted">{t("leave.summary.hours")}</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="calendar-head">
          <h3>{t("account.calendarTitle")}</h3>
          <div className="calendar-nav">
            <button
              className="ghost"
              type="button"
              onClick={() => {
                const d = new Date(
                  calendarStart.getFullYear(),
                  calendarStart.getMonth() - 1,
                  1
                );
                setCalendarStart(d);
              }}
            >
              {t("account.prev")}
            </button>
            <div className="mono">{monthRangeLabel}</div>
            <button
              className="ghost"
              type="button"
              onClick={() => {
                const d = new Date(
                  calendarStart.getFullYear(),
                  calendarStart.getMonth() + 1,
                  1
                );
                setCalendarStart(d);
              }}
            >
              {t("account.next")}
            </button>
          </div>
        </div>

        <div className="legend">
          <span className="dot approved" /> {t("leave.status.approved")}
          <span className="dot pending" /> {t("leave.status.pending")}
          <span className="dot rejected" /> {t("leave.status.rejected")}
          <span className="dot holiday" /> {safeT("account.publicHoliday", "Resmi tatil")}
        </div>

        <div className="month-strip">
          {months.map((month) => {
            const weeks = monthMatrix(month.date.getFullYear(), month.date.getMonth());
            return (
              <div className="month-card" key={month.key}>
                <div className="month-name">{month.label}</div>
                <table className="calendar" aria-label={`Leave calendar ${month.label}`}>
                  <thead>
                    <tr>
                      {weekdays.map((w) => (
                        <th key={w}>{w}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((week, wi) => (
                      <tr key={wi}>
                        {week.map((d, di) => {
                          if (!d) return <td key={di} className="empty" />;
                          const key = toLocalDateKey(d);
                          const holiday = holidayMap.get(key);
                          const nonWorkday = isWeekend(d) || Boolean(holiday);
                          const statsRaw = leaveDayMap.get(key) || { approved: 0, pending: 0, rejected: 0 };
                          const stats = nonWorkday ? { approved: 0, pending: 0, rejected: 0 } : statsRaw;
                          const cls =
                            stats.approved > 0 ? "approved" : stats.pending > 0 ? "pending" : stats.rejected > 0 ? "rejected" : "";
                          const title = holiday ? `${key} - ${holiday}` : key;
                          return (
                            <td
                              key={di}
                              className={`day ${cls} ${holiday ? "holiday" : ""} ${key === todayKey ? "today" : ""}`}
                              title={title}
                            >
                              <div className="num">{d.getDate()}</div>
                              {holiday && (
                                <div className="holiday-chip" title={holiday}>
                                  {holidayShortLabel(holiday)}
                                </div>
                              )}
                              {(stats.approved + stats.pending + stats.rejected) > 0 && (
                                <div className="marks">
                                  {stats.approved > 0 && <span className="dot approved" />}
                                  {stats.pending > 0 && <span className="dot pending" />}
                                  {stats.rejected > 0 && <span className="dot rejected" />}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      {["admin", "unit_supervisor", "company_manager", "hr"].includes(normalizeRoleName(me?.role_name)) && (
        <div className="account-grid" style={{ marginTop: 16 }}>
          <MyTeamPanel mode="home" />
          <TeamLeavesPanel />
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <SATDashboardWidget />
      </div>


      {/* Şirket Araçları - API tabanlı izin kontrolü */}
      {toolPermissions.length > 0 && toolPermissions.some((p) => p.granted) && (
        <div style={{ marginTop: 24 }}>
          <h3 className="section-title" style={{ marginBottom: 12 }}>Şirket Araçları</h3>
          <div className="account-grid">
            {toolPermissions.find((p) => p.tool_key === "tools" && p.granted) && (
              <a
                href="http://192.168.10.4:24100"
                target="_blank"
                rel="noopener noreferrer"
                className="balance-card"
                style={{ textDecoration: "none", cursor: "pointer" }}
              >
                <div className="balance-card-header">
                  <span className="balance-label">Tools</span>
                </div>
                <div className="balance-value" style={{ fontSize: "1rem" }}>
                  ComInterface Generator
                </div>
                <div style={{ fontSize: "0.8rem", opacity: 0.7, marginTop: 4 }}>
                  Mesaj ve sinyal yönetimi ↗
                </div>
              </a>
            )}
            {toolPermissions.find((p) => p.tool_key === "cms" && p.granted) && (
              <a
                href="http://192.168.10.4:4001"
                target="_blank"
                rel="noopener noreferrer"
                className="balance-card"
                style={{ textDecoration: "none", cursor: "pointer" }}
              >
                <div className="balance-card-header">
                  <span className="balance-label">BTO Website</span>
                </div>
                <div className="balance-value" style={{ fontSize: "1rem" }}>
                  Web Sitesi İçerik Yönetimi
                </div>
                <div style={{ fontSize: "0.8rem", opacity: 0.7, marginTop: 4 }}>
                  Şirket websitesi içeriklerini düzenleyin ↗
                </div>
              </a>
            )}
            {toolPermissions.find((p) => p.tool_key === "gitlab_editor" && p.granted) && (
              <a
                href="http://192.168.10.4:8088"
                target="_blank"
                rel="noopener noreferrer"
                className="balance-card"
                style={{ textDecoration: "none", cursor: "pointer" }}
              >
                <div className="balance-card-header">
                  <span className="balance-label">GitLab Editor</span>
                </div>
                <div className="balance-value" style={{ fontSize: "1rem" }}>
                  Proje & Şablon Yönetimi
                </div>
                <div style={{ fontSize: "0.8rem", opacity: 0.7, marginTop: 4 }}>
                  GitLab proje şablonlarını yönetin ↗
                </div>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
